import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import slugify from "slugify";

import Breadcrumbs from "../component/breadcrumbs";
import CustomHelmet from "../component/CustomHelmet";
import Form from "../component/Form";
import FormInput from "../component/FormInput";
import Intro from "../component/Intro";
import BaseContainer from "../component/layout/base-container";
import RegisterModal from "../lib/registerModal";
import { translate } from "../lib/translate";

export default function Signup() {
  const navigate = useNavigate();

  // States to keep track of stuff
  const [email, setEmail] = useState("");
  const [emailErrors, setEmailErrors] = useState([]);
  const [password, setPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [strictPasswordMatch, setStrictPasswordMatch] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [joinedErrors, setJoinedErrors] = useState([]);

  const [showRegisterModal, setShowRegisterModal] = useState(false);

  const checkEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email) || !email) {
      setEmailErrors(["Bitte verwenden Sie eine gültige E-Mail Adresse"]);
      return;
    }
    setEmailErrors([]);
  };

  const signup = async () => {
    checkEmail();
    try {
      await axios.post(`${process.env.REACT_APP_BASE_URL}signup`, {
        email: email,
        username: email,
        password: password,
      });
      navigate("/anmelden", {
        state: {
          message:
            "Ihr Account wurde erstellt, bitte melden Sie sich mit Ihrer E-Mail-Adresse und dem soeben gewählten Passwort an.",
          email: email,
        },
      });
    } catch (e) {
      if (e.response.status === 400) {
        const error_response = e.response.data;
        setEmailErrors(error_response["username"] || []);
        setPasswordErrors(error_response["password"] || []);
      } else {
        console.log(e);
      }
    }
  };

  const passWordMatchText = "Passwörter stimmen nicht überein.";
  const checkPasswordMatch = (strict) => {
    const checkValue = strict
      ? password === passwordConfirm
      : password.includes(passwordConfirm) ||
        passwordConfirm.includes(password);
    if (!checkValue) {
      if (!passwordErrors.includes(passWordMatchText))
        setPasswordErrors((prevState) => [...prevState, passWordMatchText]);
    } else
      setPasswordErrors((prevState) =>
        prevState.filter((el) => el !== passWordMatchText)
      );
  };

  useEffect(() => {
    if (password && passwordConfirm) checkPasswordMatch(strictPasswordMatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, passwordConfirm]);

  useEffect(
    () => setJoinedErrors(emailErrors.concat(passwordErrors)),
    [emailErrors, passwordErrors]
  );

  return (
    <>
      <CustomHelmet pageTitle="Registrieren" />
      <Breadcrumbs
        listItems={[
          { name: "Home", url: "/" },
          { name: "Registrieren", url: "/registrieren" },
        ]}
      />

      <BaseContainer>
        <Intro title="Registrieren" />
        <p>
          Haben Sie schon einen Account?{" "}
          <Link to="/anmelden" className="Link size-inherit">
            Jetzt Anmelden
          </Link>
        </p>
        <p>
          Bitte registrieren Sie sich mit Ihrer E-Mail-Adresse und einem
          Passwort
        </p>
        <Form>
          {joinedErrors.length > 0 && (
            <div className="FormMessage">
              <div
                className="FormMessage--inner js-Autofocus type-error"
                role="alert"
              >
                <div className="FormMessage--iconWrapper">
                  <span className="Icon" data-name="24--error">
                    <svg>
                      <use xlinkHref="#24--error"></use>
                    </svg>
                  </span>
                </div>
                <div className="FormMessage--body">
                  <div className="FormMessage--text richtext">
                    <p>Bei Ihrer Registrierung hat etwas nicht geklappt:</p>

                    <ul>
                      {joinedErrors.map((error) => (
                        <li key={slugify(error)}>{translate(error)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          <FormInput
            label="E-Mail"
            id="email-input"
            type="email"
            required
            error={emailErrors.length > 0}
            onInput={(e) => {
              setEmail(e.target.value);
              if (emailErrors.length > 0) checkEmail(e.target.value);
            }}
            onBlur={(e) => checkEmail(e.target.value)}
          />
          <FormInput
            label="Passwort"
            type={showPassword ? "text" : "password"}
            required
            error={passwordErrors.length > 0}
            onInput={(e) => setPassword(e.target.value)}
          />
          <button onClick={() => setShowPassword((prevState) => !prevState)}>
            {showPassword ? "Passwort verstecken" : "Passwort anzeigen"}
          </button>
          <FormInput
            label="Passwort bestätigen"
            type="password"
            required
            error={passwordErrors.length > 0}
            onKeyUp={(e) => setPasswordConfirm(e.target.value)}
            onBlur={() => setStrictPasswordMatch(true)}
          />
          <div className="FormButtons">
            <button
              disabled={!(email && password)}
              className="Button color-primary size-large mb-8"
              style={{
                marginRight: "unset",
                marginLeft: "auto",
                ...(!(email && password) && {
                  backgroundColor: "rgba(var(--c-grey-light),1)",
                  color: "rgba(var(--c-black),0.6)",
                }),
              }}
              onClick={() => setShowRegisterModal(true)}
            >
              Registrieren
            </button>
          </div>
        </Form>
      </BaseContainer>
      <RegisterModal
        registerModal={showRegisterModal}
        setRegisterModal={setShowRegisterModal}
        register={signup}
        navigate={navigate}
      />
    </>
  );
}
